import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
    fetchUserModules,
    setSelectedClient, setSelectedBranch, setSelectedUser, resetFilters,
    selectClients, selectAllBranches, selectAllUsers, selectAllModules,
    selectSelectedClient, selectSelectedBranch, selectSelectedUser, selectLoading
} from '../store/slices/usermodulesSlice';
import './CategoryMaster.css';

const UserModulesHeader = () => {
    const dispatch = useDispatch();

    const clients        = useSelector(selectClients);
    const allBranches    = useSelector(selectAllBranches);
    const allUsers       = useSelector(selectAllUsers);
    const allModules     = useSelector(selectAllModules);
    const selectedClient = useSelector(selectSelectedClient);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedUser   = useSelector(selectSelectedUser);
    const loading        = useSelector(selectLoading);

    useEffect(() => {
        dispatch(fetchUserModules());
    }, [dispatch]);

    const clientOptions = useMemo(
        () => clients.map((c) => ({ label: c.client_name, value: c.client_id })),
        [clients]
    );

    // branchs array has no client_id — derive which branches belong to a client
    // by looking at the module assignment rows (they carry both client_id and branch_id)
    const branchOptions = useMemo(() => {
        if (!selectedClient) return [];
        const branchIds = [...new Set(
            allModules
                .filter((m) => String(m.client_id) === String(selectedClient))
                .map((m) => String(m.branch_id))
                .filter(Boolean)
        )];
        return allBranches
            .filter((b) => branchIds.includes(String(b.branch_id)))
            .map((b) => ({ label: b.branch_name, value: b.branch_id }));
    }, [selectedClient, allModules, allBranches]);

    // users array has no client_id — derive via module assignment rows
    const userOptions = useMemo(() => {
        if (!selectedClient) return [];
        let filtered = allModules.filter((m) => String(m.client_id) === String(selectedClient));
        if (selectedBranch) filtered = filtered.filter((m) => String(m.branch_id) === String(selectedBranch));
        const userIds = [...new Set(filtered.map((m) => String(m.user_id)))];
        return allUsers
            .filter((u) => userIds.includes(String(u.userId)))
            .map((u) => ({ label: u.username, value: u.userId }));
    }, [selectedClient, selectedBranch, allModules, allUsers]);

    const handleReset = () => {
        dispatch(resetFilters());
        dispatch(fetchUserModules());
    };

    return (
        <>
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    User Modules
                </h2>
                <p className="text-color-secondary">Browse and manage user module assignments filtered by client, branch, and user</p>
            </div>

            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                        <label className="mb-2 font-medium text-sm">Client</label>
                        <Dropdown
                            value={selectedClient}
                            options={clientOptions}
                            onChange={(e) => dispatch(setSelectedClient(e.value))}
                            placeholder="Select Client"
                            className="w-full"
                            filter
                            showClear
                        />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                        <label className="mb-2 font-medium text-sm">Branch</label>
                        <Dropdown
                            value={selectedBranch}
                            options={branchOptions}
                            onChange={(e) => dispatch(setSelectedBranch(e.value))}
                            placeholder={selectedClient ? 'Select Branch (Optional)' : 'Select client first'}
                            className="w-full"
                            disabled={!selectedClient}
                            filter
                            showClear
                        />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                        <label className="mb-2 font-medium text-sm">User</label>
                        <Dropdown
                            value={selectedUser}
                            options={userOptions}
                            onChange={(e) => dispatch(setSelectedUser(e.value))}
                            placeholder={selectedClient ? 'Select User' : 'Select client first'}
                            className="w-full"
                            disabled={!selectedClient}
                            filter
                            showClear
                        />
                    </div>

                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={handleReset} disabled={loading} />
                    </div>
                </div>
            </Card>
        </>
    );
};

export default UserModulesHeader;
